/* eslint-disable unicorn/no-await-expression-member */

import { useCallback, useEffect, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { type IconName } from '@sg/garnish';

import type { UseStripePaymentProps } from '../../types';
import { getSelectedPaymentMethod } from './helpers';
import { useLabelAndIconByType, usePaymentMethodChangeHandler } from './hooks';

export const useStripePayment = (props: UseStripePaymentProps) => {
  const { isIntentConfirmationEnabled, paymentMethods, stripeIntentConfig } =
    props;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── State ─────────────────────────────────────────────────────────────────

  const [createdPaymentMethod, setCreatedPaymentMethod] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] =
    useState<IconName>('IconCreditCard');

  const { isApplePay, isGooglePay, isLink, isCashApp } =
    getSelectedPaymentMethod(selectedPaymentMethod);

  // ─── Payment Method Creation ───────────────────────────────────────────────

  const createPaymentMethod = useCallback(
    async (shouldSubmit = false) => {
      if (!stripe || !elements) {
        throw new Error('Stripe not initialized');
      }

      if (shouldSubmit) {
        const { error: submitError } = await elements.submit();

        if (submitError) {
          throw new Error(submitError.message);
        }
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        throw new Error(error.message);
      }

      setCreatedPaymentMethod(paymentMethod.id);

      return paymentMethod;
    },
    [elements, stripe],
  );

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();
  const handlePaymentElementChange = usePaymentMethodChangeHandler({
    paymentMethods,
    convertTypeToLabelAndIcon,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentIcon,
    createPaymentMethod,
  });

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) {
      return;
    }

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirmStripe = useCallback(async () => {
    try {
      if (!stripe || !elements) {
        return { status: 'failed', error: 'Stripe not initialized' };
      }

      const { error: submitError, selectedPaymentMethod } =
        await elements.submit();

      if (submitError) {
        return { status: 'failed', error: submitError.message };
      }

      const paymentMethodId = createdPaymentMethod || (await createPaymentMethod())?.id; // prettier-ignore

      if (isIntentConfirmationEnabled) {
        // Maps the stripe-react-native interface to web.
        return await new Promise((resolve) => {
          stripeIntentConfig?.confirmHandler?.(
            {
              id: paymentMethodId,
              paymentMethodType: selectedPaymentMethod,
            } as never,
            false,
            async ({ clientSecret = '' }) => {
              const result = await stripe.confirmPayment({
                elements,
                clientSecret,
                redirect: redirectCondition,
                confirmParams: { return_url: defaultRedirect },
              });

              if (result?.error) {
                resolve({ status: 'failed', error: result.error.message });
                return;
              }

              resolve({ status: 'completed' });
            },
          );
        });
      }

      if (!paymentMethodId) {
        return { status: 'failed', error: 'No payment method created' };
      }

      return { status: 'completed', paymentMethodId };
    } catch (error) {
      return { status: 'failed', error: (error as Error).message };
    }
  }, [
    stripe,
    elements,
    isIntentConfirmationEnabled,
    createdPaymentMethod,
    createPaymentMethod,
    stripeIntentConfig,
  ]);

  return {
    isGooglePay,
    isApplePay,
    isLink,
    isCashApp,
    selectedPaymentMethod,
    selectedPaymentIcon,
    confirmStripe,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const redirectCondition = 'if_required' as const;
const defaultRedirect = window.location.origin;
